import React, {useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {Alert, Button, Modal, Spinner, Tab, Tabs} from 'react-bootstrap';
import {FaAngleLeft, FaAngleRight, FaCropAlt, FaDownload, FaEllipsisV, FaFire, FaPlayCircle, FaRedo} from 'react-icons/fa';
import {useTranslation} from 'react-i18next';
import * as api from '../api';

const formatValue = (value, suffix = '') => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  return `${value}${suffix}`;
};

const formatDistanceMeters = value => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  const meters = Number(value) * 0.3048;
  if (!Number.isFinite(meters)) {
    return '-';
  }
  return `${Math.round(meters)} m`;
};

const formatTime = ms => {
  const seconds = Math.max(0, Math.floor(Number(ms || 0) / 1000));
  return new Date(seconds * 1000).toISOString().slice(11, 19);
};

const teamLabel = (team, t) => {
  if (team === null || team === undefined || team === '') {
    return '-';
  }
  return t('ANALYSIS_TEAM_NUMBER', {number: Number(team) + 1});
};

const playerLabel = (playerId, t) => {
  if (playerId === null || playerId === undefined || playerId === '') {
    return '-';
  }
  return t('ANALYSIS_PLAYER_NUMBER', {number: Number(playerId) + 1});
};

const defaultPlayerName = (player, t) => (
  player && player.defaultName ? player.defaultName : playerLabel(player && player.id, t)
);

const scoreToDraft = score => {
  const normalizeScorePart = value => (value === null || value === undefined || value === '' ? '' : String(value));
  if (Array.isArray(score)) {
    return {
      team1: normalizeScorePart(score[0]),
      team2: normalizeScorePart(score[1])
    };
  }
  if (score && typeof score === 'object') {
    const nestedScore = score.score || score.scores || score.teamScores || score.team_scores || score.game_score;
    if (nestedScore && nestedScore !== score) {
      return scoreToDraft(nestedScore);
    }
    return {
      team1: normalizeScorePart(score.team1 ?? score.team_1 ?? score.team0 ?? score.team_0 ?? score[0]),
      team2: normalizeScorePart(score.team2 ?? score.team_2 ?? score.team1Score ?? score.team_1_score ?? score[1])
    };
  }
  if (typeof score === 'string') {
    const parts = score.split(/[-:]/).map(part => part.trim()).filter(Boolean);
    if (parts.length >= 2) {
      return {team1: parts[0], team2: parts[1]};
    }
  }
  return {team1: '', team2: ''};
};

const formatSegmentScore = score => {
  const draft = scoreToDraft(score);
  if (!draft.team1 && !draft.team2) {
    return '-';
  }
  return `${draft.team1 || '0'} - ${draft.team2 || '0'}`;
};

const LABELS = {
  dink_consistency: 'ANALYSIS_DINK_CONSISTENCY', drop_shot_consistency: 'ANALYSIS_DROP_CONSISTENCY', legal_serve_percentage: 'ANALYSIS_LEGAL_SERVES', legal_third_shot_percentage: 'ANALYSIS_LEGAL_THIRD_SHOTS', kitchen_arrival_percentage_on_serve: 'ANALYSIS_KITCHEN_ARRIVAL_SERVE', rallies_won_with_resets: 'ANALYSIS_RALLIES_WON_RESETS', rallies_won_with_speedups: 'ANALYSIS_RALLIES_WON_SPEEDUPS', return_quality: 'ANALYSIS_RETURN_QUALITY', serve_quality: 'ANALYSIS_SERVE_QUALITY', third_shot_quality: 'ANALYSIS_THIRD_SHOT_QUALITY', long_battle: 'ANALYSIS_LONG_EXCHANGE', firefight: 'ANALYSIS_FAST_EXCHANGE', passing: 'ANALYSIS_PASSING', poach: 'ANALYSIS_POACH', sequence: 'ANALYSIS_SEQUENCE', drive: 'ANALYSIS_DRIVE', drop: 'ANALYSIS_DROP', dink: 'ANALYSIS_DINK', smash: 'ANALYSIS_SMASH', overhead: 'ANALYSIS_OVERHEAD', volley: 'ANALYSIS_VOLLEY', return: 'ANALYSIS_RETURN', serve: 'ANALYSIS_SERVE'
};

const translateToken = (value, t) => {
  if (!value) {
    return '-';
  }
  const key = String(value).trim().toLowerCase().replace(/\s+/g, '_');
  if (LABELS[key]) {
    return t(LABELS[key]);
  }
  return key
    .split('_')
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const translateText = (value, t) => {
  if (!value) {
    return '-';
  }
  const key = String(value).trim().toLowerCase().replace(/\s+/g, '_');
  if (LABELS[key]) {
    return t(LABELS[key]);
  }
  return String(value)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, letter => letter.toUpperCase());
};

const metricCards = (overview, t) => [
  {label: t('ANALYSIS_SCORE'), value: Array.isArray(overview.score) ? overview.score.join(' - ') : overview.score},
  {label: t('ANALYSIS_RALLIES'), value: overview.totalRallies},
  {label: t('ANALYSIS_TOTAL_SHOTS'), value: overview.totalShots},
  {label: t('ANALYSIS_AVERAGE_SHOTS_RALLY'), value: overview.averageShotsPerRally},
  {label: t('ANALYSIS_KITCHEN_RALLIES'), value: overview.kitchenRallies},
  {label: t('ANALYSIS_LONGEST_RALLY'), value: overview.longestRallyShots}
];

const SEGMENT_INTRO_MS = 1150;

const PlayerVideoAnalysisReport = ({show, onHide, request, token, providerUrl}) => {
  const {t} = useTranslation();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentMs, setCurrentMs] = useState(0);
  const [activeSegment, setActiveSegment] = useState(null);
  const [segmentIntro, setSegmentIntro] = useState(null);
  const [playbackMode, setPlaybackMode] = useState('full');
  const [activePlaylistIndex, setActivePlaylistIndex] = useState(0);
  const [scoreDraft, setScoreDraft] = useState({team1: '', team2: ''});
  const [savingScore, setSavingScore] = useState(false);
  const [scoreSaveError, setScoreSaveError] = useState(null);
  const [playerNames, setPlayerNames] = useState({});
  const [savingPlayers, setSavingPlayers] = useState(false);
  const [playerSaveError, setPlayerSaveError] = useState(null);
  const videoRef = useRef(null);
  const segmentIntroTimerRef = useRef(null);

  useEffect(() => () => {
    if (segmentIntroTimerRef.current) {
      clearTimeout(segmentIntroTimerRef.current);
    }
  }, []);

  useEffect(() => {
    if (!show || !request || !request.id || !token) {
      return;
    }

    setLoading(true);
    setError(null);
    api.getVideoAnalysisReport(request.id, token).then(result => {
      setLoading(false);
      if (result.status >= 200 && result.status < 300) {
        setReport(result.json);
        const nextNames = {};
        (result.json.players || []).forEach(player => {
          nextNames[player.id] = player.name || defaultPlayerName(player, t);
        });
        setPlayerNames(nextNames);
        setCurrentMs(0);
        setActiveSegment(null);
        setSegmentIntro(null);
        setPlaybackMode('full');
        setActivePlaylistIndex(0);
        setScoreDraft({team1: '', team2: ''});
        setScoreSaveError(null);
        setPlayerSaveError(null);
        return;
      }
      setReport(null);
      setError(t('ANALYSIS_REPORT_LOAD_ERROR'));
    });
  }, [show, request, t, token]);

  const overview = report && report.overview ? report.overview : {};
  const players = report && Array.isArray(report.players) ? report.players : [];
  const rallies = report && Array.isArray(report.rallies) ? report.rallies : [];
  const highlights = report && Array.isArray(report.highlights) ? report.highlights : [];
  const orderedHighlights = useMemo(() => {
    const counters = {};
    return highlights
      .slice()
      .sort((a, b) => Number(a.startMs || 0) - Number(b.startMs || 0))
      .map(highlight => {
        const baseTitle = translateText(highlight.title || highlight.kind || 'Highlight', t);
        counters[baseTitle] = (counters[baseTitle] || 0) + 1;
        return {
          ...highlight,
          displayTitle: `${baseTitle} #${counters[baseTitle]}`
        };
      });
  }, [highlights, t]);
  const coachAdvice = report && Array.isArray(report.coachAdvice) ? report.coachAdvice : [];
  const shots = report && Array.isArray(report.shots) ? report.shots : [];
  const videoUrl = report && report.video ? report.video.url : null;
  const durationMs = useMemo(() => {
    const lastRallyEnd = rallies.reduce((max, rally) => Math.max(max, Number(rally.endMs || 0)), 0);
    const reportDuration = report && report.video ? Number(report.video.durationSeconds || 0) * 1000 : 0;
    return Math.max(lastRallyEnd, reportDuration, 1);
  }, [rallies, report]);
  const activeRally = rallies.find(rally => currentMs >= Number(rally.startMs || 0) && currentMs <= Number(rally.endMs || 0));
  const activeRallyIndex = activeRally ? rallies.findIndex(rally => rally.id === activeRally.id) : -1;
  const selectedSegment = activeSegment || activeRally;
  const playlistSegments = playbackMode === 'highlights' ? orderedHighlights : rallies;
  const selectedRallyNumber = selectedSegment && selectedSegment.number
    ? selectedSegment.number
    : activeRally && activeRally.number
      ? activeRally.number
      : rallies.length > 0
        ? rallies[0].number
        : '-';
  const selectedRallyShots = activeRally
    ? shots.filter(shot => Number(shot.rallyIndex) === Number(activeRally.id))
    : [];
  const displayPlayerName = playerId => {
    const player = players.find(item => Number(item.id) === Number(playerId));
    if (!player) {
      return playerLabel(playerId, t);
    }
    return playerNames[player.id] || player.name || defaultPlayerName(player, t);
  };
  const playersByTeam = useMemo(() => {
    const grouped = {};
    players.forEach(player => {
      const key = player.teamNumber || Number(player.team) + 1 || 0;
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(player);
    });
    return Object.keys(grouped)
      .sort((a, b) => Number(a) - Number(b))
      .map(teamNumber => ({
        teamNumber,
        players: grouped[teamNumber].sort((a, b) => Number(a.teamPlayerNumber || 0) - Number(b.teamPlayerNumber || 0))
      }));
  }, [players]);
  const displayScoreSegment = activeRally || null;
  const displayScore = displayScoreSegment ? displayScoreSegment.manualScore || displayScoreSegment.score : overview.score;
  const scoreDraftForDisplay = scoreToDraft(displayScore);
  const scoreboardRows = [1, 2].map(teamNumber => {
    const teamGroup = playersByTeam.find(group => Number(group.teamNumber) === teamNumber);
    const teamNames = teamGroup && teamGroup.players.length > 0
      ? teamGroup.players.map(player => displayPlayerName(player.id)).join(' / ')
      : `Team ${teamNumber}`;
    return {
      teamNumber,
      name: teamNames,
      score: teamNumber === 1 ? scoreDraftForDisplay.team1 : scoreDraftForDisplay.team2
    };
  });
  const playerHasChanges = players.some(player => (playerNames[player.id] || '') !== (player.name || defaultPlayerName(player, t)));
  const selectedScore = selectedSegment ? selectedSegment.manualScore || selectedSegment.score : overview.score;
  const selectedSegmentType = selectedSegment && selectedSegment.segmentType === 'highlight' ? 'highlights' : 'rallies';
  const selectedSegmentId = selectedSegment ? selectedSegment.id : null;
  const scoreHasChanges = selectedSegment
    ? scoreDraft.team1 !== scoreToDraft(selectedScore).team1 || scoreDraft.team2 !== scoreToDraft(selectedScore).team2
    : false;

  useEffect(() => {
    setScoreDraft(scoreToDraft(selectedScore));
    setScoreSaveError(null);
  }, [selectedSegmentId, selectedSegmentType, selectedScore]);

  const changePlayerName = playerId => event => {
    const value = event.target.value;
    setPlayerNames(current => ({
      ...current,
      [playerId]: value
    }));
  };

  const savePlayerNames = async event => {
    event.preventDefault();
    if (!request || !request.id) {
      return;
    }
    setSavingPlayers(true);
    setPlayerSaveError(null);
    const payload = {};
    players.forEach(player => {
      const name = (playerNames[player.id] || '').trim();
      payload[player.id] = {name};
    });
    const result = await api.updateVideoAnalysisPlayers(request.id, payload, token);
    setSavingPlayers(false);
    if (result.status >= 200 && result.status < 300) {
      setReport(result.json);
      const nextNames = {};
      (result.json.players || []).forEach(player => {
        nextNames[player.id] = player.name || defaultPlayerName(player, t);
      });
      setPlayerNames(nextNames);
      return;
    }
    setPlayerSaveError(t('ANALYSIS_PLAYER_NAMES_SAVE_ERROR'));
  };

  const changeScore = key => event => {
    const value = event.target.value.replace(/[^\d]/g, '').slice(0, 3);
    setScoreDraft(current => ({
      ...current,
      [key]: value
    }));
  };

  const persistSegmentScore = async nextDraft => {
    if (!request || !request.id || selectedSegmentId === null || selectedSegmentId === undefined) {
      return;
    }
    const nextScores = {
      rallies: {...((report.segmentScores || {}).rallies || {})},
      highlights: {...((report.segmentScores || {}).highlights || {})}
    };
    const nextScore = {
      team1: nextDraft.team1.trim(),
      team2: nextDraft.team2.trim()
    };
    if (nextScore.team1 || nextScore.team2) {
      nextScores[selectedSegmentType][String(selectedSegmentId)] = nextScore;
    } else {
      delete nextScores[selectedSegmentType][String(selectedSegmentId)];
    }

    setSavingScore(true);
    setScoreSaveError(null);
    const result = await api.updateVideoAnalysisSegmentScores(request.id, nextScores, token);
    setSavingScore(false);
    if (result.status >= 200 && result.status < 300) {
      setReport(result.json);
      const updatedSegments = selectedSegmentType === 'highlights' ? result.json.highlights || [] : result.json.rallies || [];
      const updatedSegment = updatedSegments.find(segment => Number(segment.id) === Number(selectedSegmentId));
      if (updatedSegment) {
        setActiveSegment(updatedSegment);
        setScoreDraft(scoreToDraft(updatedSegment.manualScore || updatedSegment.score));
      }
      return;
    }
    setScoreSaveError(t('ANALYSIS_SCORE_SAVE_ERROR'));
  };

  const saveSegmentScore = async event => {
    event.preventDefault();
    await persistSegmentScore(scoreDraft);
  };

  const restoreProviderScore = event => {
    event.preventDefault();
    persistSegmentScore({team1: '', team2: ''});
  };

  const segmentDisplayTitle = segment => (
    segment && (segment.displayTitle || translateText(segment.title || `Rally ${segment.number || Number(segment.rallyIndex) + 1 || ''}`, t))
  );

  const clearSegmentIntroTimer = () => {
    if (segmentIntroTimerRef.current) {
      clearTimeout(segmentIntroTimerRef.current);
      segmentIntroTimerRef.current = null;
    }
  };

  const jumpToSegment = (segment, shouldPlay = true) => {
    clearSegmentIntroTimer();
    setSegmentIntro(null);
    const video = videoRef.current;
    if (!video || !segment) {
      return;
    }
    const startMs = Number(segment.startMs || 0);
    video.currentTime = startMs / 1000;
    setCurrentMs(startMs);
    setActiveSegment(segment);
    if (shouldPlay) {
      video.play().catch(() => {});
    }
  };

  const playSegmentWithIntro = (segment, nextMode, nextIndex = null) => {
    const video = videoRef.current;
    if (!segment) {
      return;
    }
    if (video) {
      video.pause();
    }
    clearSegmentIntroTimer();
    if (nextMode) {
      setPlaybackMode(nextMode);
    }
    if (nextIndex !== null && nextIndex !== undefined) {
      setActivePlaylistIndex(nextIndex);
    }
    setActiveSegment(segment);
    setCurrentMs(Number(segment.startMs || 0));
    setSegmentIntro({
      title: segmentDisplayTitle(segment),
      score: formatSegmentScore(segment.manualScore || segment.score),
      type: nextMode === 'highlights' ? 'Highlight' : 'Rally'
    });
    segmentIntroTimerRef.current = setTimeout(() => {
      segmentIntroTimerRef.current = null;
      setSegmentIntro(null);
      jumpToSegment(segment, true);
    }, SEGMENT_INTRO_MS);
  };

  const seekTo = (segment, shouldPlay = true, nextMode = null, nextIndex = null) => event => {
    if (event) {
      event.preventDefault();
    }
    if (shouldPlay && nextMode && nextMode !== 'full') {
      playSegmentWithIntro(segment, nextMode, nextIndex);
      return;
    }
    if (nextMode) {
      setPlaybackMode(nextMode);
    }
    if (nextIndex !== null && nextIndex !== undefined) {
      setActivePlaylistIndex(nextIndex);
    }
    jumpToSegment(segment, shouldPlay);
  };

  const changePlaybackMode = nextMode => event => {
    if (event) {
      event.preventDefault();
    }
    setPlaybackMode(nextMode);
    setActivePlaylistIndex(0);
    if (nextMode === 'full') {
      clearSegmentIntroTimer();
      setSegmentIntro(null);
      setActiveSegment(null);
      return;
    }
    const nextSegments = nextMode === 'highlights' ? orderedHighlights : rallies;
    if (nextSegments.length > 0) {
      playSegmentWithIntro(nextSegments[0], nextMode, 0);
    }
  };

  const seekRallyByDelta = delta => event => {
    if (event) {
      event.preventDefault();
    }
    if (rallies.length === 0) {
      return;
    }
    const baseIndex = activeRallyIndex >= 0 ? activeRallyIndex : 0;
    const nextIndex = Math.max(0, Math.min(rallies.length - 1, baseIndex + delta));
    playSegmentWithIntro(rallies[nextIndex], 'rallies', nextIndex);
  };

  const replaySegment = event => {
    if (event) {
      event.preventDefault();
    }
    const segment = selectedSegment || rallies[0];
    if (segment) {
      jumpToSegment(segment);
    }
  };

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    const nextMs = video.currentTime * 1000;
    setCurrentMs(nextMs);
    if (activeSegment && activeSegment.endMs && nextMs >= activeSegment.endMs) {
      if (playbackMode !== 'full' && playlistSegments.length > 0) {
        const currentIndex = playlistSegments.findIndex(segment => segment.id === activeSegment.id);
        const baseIndex = currentIndex >= 0 ? currentIndex : activePlaylistIndex;
        const nextIndex = baseIndex + 1;
        if (nextIndex < playlistSegments.length) {
          playSegmentWithIntro(playlistSegments[nextIndex], playbackMode, nextIndex);
          return;
        }
      }
      video.pause();
      setActiveSegment(null);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered dialogClassName="analysis-report-modal">
      <Modal.Header closeButton>
        <Modal.Title as="h5">{request ? request.title || t('ANALYSIS_REPLAYER') : t('ANALYSIS_REPLAYER')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="analysis-report-loading">
            <Spinner animation="border" size="sm" className="mr-2"/>
            {t('ANALYSIS_LOADING_REPLAYER')}
          </div>
        ) : null}
        {error ? <Alert variant="danger">{error}</Alert> : null}
        {report && (!report.hasInsights || !report.hasStats) ? (
          <Alert variant="warning">
            {t('ANALYSIS_INCOMPLETE_DATA_HELP')}
          </Alert>
        ) : null}
        {report ? (
          <div className="analysis-report">
            <div className="analysis-report-hero">
              <div>
                <h4>{report.title}</h4>
                {report.description ? <p>{report.description}</p> : null}
              </div>
              <div className="analysis-report-provider">
                <span>{t('ANALYSIS_VIDEO_ID')}</span>
                <strong>{formatValue(report.providerVideoId)}</strong>
              </div>
            </div>
            <div className="analysis-viewer">
              <div className="analysis-viewer-main">
                <div className="analysis-video-shell">
                  {videoUrl ? (
                    <video ref={videoRef} src={videoUrl} controls playsInline onTimeUpdate={handleTimeUpdate}/>
                  ) : (
                    <div className="analysis-video-empty">
                      {t('ANALYSIS_VIDEO_UNAVAILABLE')}
                    </div>
                  )}
                  {activeRally ? (
                    <div className="analysis-video-badge">{t('ANALYSIS_RALLY_NUMBER', {number: activeRally.number})}</div>
                  ) : null}
                  {formatSegmentScore(displayScore) !== '-' ? (
                    <div className="analysis-video-score">
                      {scoreboardRows.map(row => (
                        <div className="analysis-video-score-row" key={row.teamNumber}>
                          <span>{row.name}</span>
                          <strong>{row.score || '0'}</strong>
                        </div>
                      ))}
                    </div>
                  ) : null}
                  {segmentIntro ? (
                    <div className="analysis-segment-intro">
                      <span>{segmentIntro.type}</span>
                      <strong>{segmentIntro.title}</strong>
                      {segmentIntro.score !== '-' ? <em>{segmentIntro.score}</em> : null}
                    </div>
                  ) : null}
                </div>
                <div className="analysis-viewer-controls">
                  <div className="analysis-rally-stepper">
                    <Button className="analysis-control-btn icon" onClick={seekRallyByDelta(-1)} disabled={rallies.length === 0} title={t('ANALYSIS_PREVIOUS_RALLY')}>
                      <FaAngleLeft/>
                    </Button>
                    <div className="analysis-rally-label">{t('ANALYSIS_RALLY_NUMBER', {number: selectedRallyNumber})}</div>
                    <Button className="analysis-control-btn icon" onClick={seekRallyByDelta(1)} disabled={rallies.length === 0} title={t('ANALYSIS_NEXT_RALLY')}>
                      <FaAngleRight/>
                    </Button>
                  </div>
                  <div className="analysis-rally-actions">
                    <Button className="analysis-control-btn icon muted" disabled title={t('ANALYSIS_CROP')}>
                      <FaCropAlt/>
                    </Button>
                    <Button className="analysis-control-btn icon muted" disabled title={t('DOWNLOAD')}>
                      <FaDownload/>
                    </Button>
                    <span className="analysis-toggle-switch" aria-hidden="true"><span/></span>
                    <Button className="analysis-control-btn icon" onClick={replaySegment} disabled={!selectedSegment && rallies.length === 0} title={t('ANALYSIS_REPLAY_RALLY')}>
                      <FaRedo/>
                    </Button>
                  </div>
                  {selectedSegment ? (
                    <div className="analysis-current-segment">
                      <strong>{selectedSegment.displayTitle || translateText(selectedSegment.title || `Rally ${selectedSegment.number || Number(selectedSegment.rallyIndex) + 1 || ''}`, t)}</strong>
                      <span>{formatTime(selectedSegment.startMs)} - {formatTime(selectedSegment.endMs)}{activeRally ? ` | ${t('ANALYSIS_SHOTS_COUNT', {count: selectedRallyShots.length || activeRally.shotCount})}` : ''}</span>
                    </div>
                  ) : null}
                  {selectedSegment ? (
                    <form className="analysis-score-editor" onSubmit={saveSegmentScore}>
                      <label>
                        {t('ANALYSIS_TEAM_NUMBER', {number: 1})}
                        <input type="text" inputMode="numeric" value={scoreDraft.team1} onChange={changeScore('team1')} placeholder="0"/>
                      </label>
                      <span>-</span>
                      <label>
                        {t('ANALYSIS_TEAM_NUMBER', {number: 2})}
                        <input type="text" inputMode="numeric" value={scoreDraft.team2} onChange={changeScore('team2')} placeholder="0"/>
                      </label>
                      <Button className="analysis-score-save" type="submit" disabled={savingScore || !scoreHasChanges}>
                        {savingScore ? t('SAVING') : t('ANALYSIS_SAVE_SCORE')}
                      </Button>
                      {selectedSegment.manualScore ? (
                        <Button className="analysis-score-reset" onClick={restoreProviderScore} disabled={savingScore}>
                          {t('ANALYSIS_USE_PB_VISION')}
                        </Button>
                      ) : null}
                      {scoreSaveError ? <strong>{scoreSaveError}</strong> : null}
                    </form>
                  ) : null}
                  <div className="analysis-playback-modes">
                    <Button className={`analysis-mode-btn ${playbackMode === 'full' ? 'active' : ''}`} onClick={changePlaybackMode('full')}>
                      {t('ANALYSIS_FULL_MATCH')}
                    </Button>
                    <Button className={`analysis-mode-btn ${playbackMode === 'rallies' ? 'active' : ''}`} onClick={changePlaybackMode('rallies')} disabled={rallies.length === 0}>
                      {t('ANALYSIS_RALLIES_ONLY')}
                    </Button>
                    <Button className={`analysis-mode-btn ${playbackMode === 'highlights' ? 'active' : ''}`} onClick={changePlaybackMode('highlights')} disabled={orderedHighlights.length === 0}>
                      {t('ANALYSIS_HIGHLIGHTS_ONLY')}
                    </Button>
                  </div>
                </div>
                <div className="analysis-timeline">
                  <div className="analysis-timeline-track">
                    {rallies.map(rally => {
                      const left = Math.max(0, (Number(rally.startMs || 0) / durationMs) * 100);
                      const width = Math.max(0.8, ((Number(rally.endMs || 0) - Number(rally.startMs || 0)) / durationMs) * 100);
                      const isCurrent = activeRally && activeRally.id === rally.id;
                      return (
                        <button
                          type="button"
                          key={rally.id}
                          className={`analysis-timeline-rally team-${rally.winningTeam} ${isCurrent ? 'current' : ''}`}
                          style={{left: `${left}%`, width: `${Math.min(width, 100 - left)}%`}}
                          onClick={seekTo(rally, true, 'rallies', rallies.findIndex(item => item.id === rally.id))}
                          title={`${t('ANALYSIS_RALLY_NUMBER', {number: rally.number})} | ${t('ANALYSIS_SHOTS_COUNT', {count: rally.shotCount})} | ${teamLabel(rally.winningTeam, t)}`}
                        >
                          {rally.number}
                        </button>
                      );
                    })}
                    <div className="analysis-timeline-playhead" style={{left: `${Math.min(100, (currentMs / durationMs) * 100)}%`}}/>
                  </div>
                  <div className="analysis-timeline-labels">
                    <span>{formatTime(0)}</span>
                    <span>{formatTime(currentMs)}</span>
                    <span>{formatTime(durationMs)}</span>
                  </div>
                </div>
                <div className="analysis-highlights-strip">
                  <div className="analysis-side-title analysis-highlights-title">{t('ANALYSIS_HIGHLIGHTS')}</div>
                  <div className="analysis-side-list horizontal">
                    {orderedHighlights.slice(0, 16).map((highlight, index) => (
                      <button type="button" className="analysis-side-item analysis-highlight-tile" key={highlight.id} onClick={seekTo(highlight, true, 'highlights', index)}>
                        <span className={index === 0 ? 'analysis-highlight-icon hot' : 'analysis-highlight-icon'}>
                          {index === 0 ? <FaFire/> : <FaPlayCircle/>}
                        </span>
                        <strong>{highlight.displayTitle}</strong>
                        <span className="analysis-highlight-menu"><FaEllipsisV/></span>
                      </button>
                    ))}
                    {orderedHighlights.length === 0 ? <div className="analysis-empty">{t('ANALYSIS_NO_HIGHLIGHTS')}</div> : null}
                  </div>
                </div>
              </div>
            </div>
            <Tabs defaultActiveKey="overview" className="analysis-report-tabs">
              <Tab eventKey="overview" title={t('ANALYSIS_OVERVIEW')}>
                <div className="analysis-report-metrics">
                  {metricCards(overview, t).map(metric => (
                    <div className="analysis-report-metric" key={metric.label}>
                      <span>{metric.label}</span>
                      <strong>{formatValue(metric.value)}</strong>
                    </div>
                  ))}
                </div>
                <div className="analysis-report-section">
                  <h5>{t('ANALYSIS_PLAYERS')}</h5>
                  <div className="analysis-team-grid">
                    {playersByTeam.map(group => (
                      <div className="analysis-team-panel" key={group.teamNumber}>
                        <h6>{t('ANALYSIS_TEAM_NUMBER', {number: group.teamNumber})}</h6>
                        <div className="analysis-player-grid">
                          {group.players.map(player => (
                            <div className="analysis-player-card" key={player.id}>
                              <div className="analysis-player-header">
                                <div className="analysis-player-avatar-wrap">
                                  {player.avatarUrl ? (
                                    <img className="analysis-player-avatar" src={player.avatarUrl} alt={defaultPlayerName(player, t)}/>
                                  ) : (
                                    <div className="analysis-player-avatar fallback">T{player.teamNumber || '-'}<br/>G{player.teamPlayerNumber || '-'}</div>
                                  )}
                                </div>
                                <div className="analysis-player-title">
                                  <strong>{displayPlayerName(player.id)}</strong>
                                  <span>{t('ANALYSIS_PLAYER_NUMBER', {number: player.teamPlayerNumber || '-'})}</span>
                                </div>
                              </div>
                              <label className="analysis-player-name-field">
                                {t('ANALYSIS_PLAYER_NAME')}
                                <input
                                  type="text"
                                  maxLength="80"
                                  value={playerNames[player.id] || ''}
                                  placeholder={defaultPlayerName(player, t)}
                                  onChange={changePlayerName(player.id)}
                                />
                              </label>
                              <dl>
                                <dt>{t('ANALYSIS_SHOTS')}</dt><dd>{formatValue(player.shotCount)}</dd>
                                <dt>{t('ANALYSIS_AVERAGE_QUALITY')}</dt><dd>{formatValue(player.averageShotQuality, '%')}</dd>
                                <dt>{t('ANALYSIS_DISTANCE')}</dt><dd>{formatDistanceMeters(player.distanceCovered)}</dd>
                                <dt>{t('ANALYSIS_VOLLEY_GROUND')}</dt><dd>{formatValue(player.volleyCount)} / {formatValue(player.groundStrokeCount)}</dd>
                                <dt>{t('ANALYSIS_NET_OUT_ERRORS')}</dt><dd>{formatValue(player.netFaultPercentage, '%')} / {formatValue(player.outFaultPercentage, '%')}</dd>
                              </dl>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="analysis-player-save-bar">
                    {playerSaveError ? <span className="analysis-player-save-error">{playerSaveError}</span> : <span>{t('ANALYSIS_PLAYER_NAMES_HELP')}</span>}
                    <Button className="btn btn_red_rp" onClick={savePlayerNames} disabled={savingPlayers || !playerHasChanges}>
                      {savingPlayers ? t('SAVING') : t('ANALYSIS_SAVE_PLAYER_NAMES')}
                    </Button>
                  </div>
                </div>
              </Tab>
              <Tab eventKey="rallies" title={t('ANALYSIS_RALLIES')}>
                <div className="analysis-table-wrap">
                  <table className="analysis-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>{t('ANALYSIS_TIME')}</th>
                        <th>{t('ANALYSIS_SHOTS')}</th>
                        <th>{t('ANALYSIS_WINNER')}</th>
                        <th>{t('ANALYSIS_FINAL_SHOT')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rallies.map(rally => (
                        <tr key={rally.id}>
                          <td>{rally.number}</td>
                          <td>{formatTime(rally.startMs)} - {formatTime(rally.endMs)}</td>
                          <td>{rally.shotCount}</td>
                          <td>{teamLabel(rally.winningTeam, t)}</td>
                          <td>{translateText(rally.finalShot, t)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Tab>
              <Tab eventKey="shots" title={t('ANALYSIS_SHOTS')}>
                <div className="analysis-table-wrap">
                  <table className="analysis-table">
                    <thead>
                      <tr>
                        <th>{t('ANALYSIS_RALLY')}</th>
                        <th>{t('ANALYSIS_TIME')}</th>
                        <th>{t('ANALYSIS_PLAYER')}</th>
                        <th>{t('ANALYSIS_SHOT')}</th>
                        <th>{t('ANALYSIS_QUALITY')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shots.map(shot => (
                        <tr key={shot.id}>
                          <td>{Number(shot.rallyIndex) + 1}</td>
                          <td>{formatTime(shot.startMs)}</td>
                          <td>{displayPlayerName(shot.playerId)}</td>
                          <td>{translateText(shot.label, t)}</td>
                          <td>{formatValue(shot.quality, '%')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Tab>
              <Tab eventKey="highlights" title={t('ANALYSIS_HIGHLIGHTS')}>
                <div className="analysis-highlight-list">
                  {orderedHighlights.map((highlight, index) => (
                    <button type="button" className="analysis-highlight-item" key={highlight.id} onClick={seekTo(highlight, true, 'highlights', index)}>
                      <div>
                        <strong>{highlight.displayTitle}</strong>
                        <span>{t('ANALYSIS_RALLY_NUMBER', {number: Number(highlight.rallyIndex) + 1 || '-'})} | {formatTime(highlight.startMs)} - {formatTime(highlight.endMs)}</span>
                      </div>
                      <span>{formatValue((highlight.events || []).map(value => translateToken(value, t)).join(', '))}</span>
                    </button>
                  ))}
                  {orderedHighlights.length === 0 ? <div className="analysis-empty">{t('ANALYSIS_NO_HIGHLIGHTS')}</div> : null}
                </div>
              </Tab>
              <Tab eventKey="advice" title={t('ANALYSIS_ADVICE')}>
                <div className="analysis-advice-list">
                  {coachAdvice.map((advice, index) => (
                    <div className="analysis-advice-item" key={`${advice.playerId}-${index}`}>
                      <strong>{translateToken(advice.metric || advice.title, t)}</strong>
                      <span>{advice.message || t('ANALYSIS_ADVICE_FALLBACK', {player: displayPlayerName(advice.playerId), priority: formatValue(advice.relevance, '%'), value: formatValue(advice.value, '%')})}</span>
                    </div>
                  ))}
                  {coachAdvice.length === 0 ? <div className="analysis-empty">{t('ANALYSIS_NO_ADVICE')}</div> : null}
                </div>
              </Tab>
            </Tabs>
          </div>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        {providerUrl ? (
          <a href={providerUrl} target="_blank" rel="noopener noreferrer" className="btn btn_red_outline_rp">
            {t('ANALYSIS_OPEN_PB_VISION')}
          </a>
        ) : null}
        <Button className="btn btn_red_rp" onClick={onHide}>{t('CLOSE_BUTTON')}</Button>
      </Modal.Footer>
    </Modal>
  );
};

PlayerVideoAnalysisReport.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  request: PropTypes.object,
  token: PropTypes.string,
  providerUrl: PropTypes.string
};

PlayerVideoAnalysisReport.defaultProps = {
  request: null,
  token: null,
  providerUrl: null
};

export default PlayerVideoAnalysisReport;
